import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, X, Loader2, RefreshCw, Save } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { modelServicePost } from '../../utils/modelService';
import { apiPatch } from '../../utils/api';
import toast from 'react-hot-toast';

// AI-drafted, human-reviewed personalized opening for one engagement's digest.
// The draft replaces only the greeting/intro paragraphs of the email — the
// property table, CSV checklists, and portal instructions are always appended
// by the mailer. Saving stores it on the engagement; sending stays manual.
const OutreachDraftModal = ({ row, auditYear, onClose, onSaved }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [draft, setDraft] = useState(row.custom_message || '');
  const [instructions, setInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const generate = useCallback(async (extra) => {
    setGenerating(true);
    try {
      const res = await modelServicePost('/audit/ai/draft-outreach', {
        contact_name: row.contact_name,
        owner_name: row.owner_company_name,
        audit_year: auditYear,
        state: row.state,
        reminder_step: row.reminder_step,
        last_sent_at: row.last_sent_at,
        properties: (row.properties || []).map((p) => ({ name: p.name, units_outstanding: p.units_outstanding })),
        instructions: extra || undefined,
      });
      if (!res.ok) throw new Error('Draft failed');
      const json = await res.json();
      setDraft(json.draft || '');
    } catch {
      toast.error('Could not generate a draft');
    } finally {
      setGenerating(false);
    }
  }, [row, auditYear]);

  // Generate on open when there's no saved message yet.
  useEffect(() => {
    if (!row.custom_message) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (message) => {
    setSaving(true);
    try {
      const res = await apiPatch(`/admin/audit_tracker/outreach_engagements/${row.id}`, { custom_message: message });
      if (!res.ok) throw new Error();
      toast.success(message ? 'Draft saved — it will be used on the next send' : 'Custom message cleared');
      onSaved?.();
      onClose();
    } catch {
      toast.error('Could not save the draft');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="AI outreach draft" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-2xl rounded-xl border shadow-xl flex flex-col max-h-[90vh] ${d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
      >
        <div className={`flex items-center justify-between px-5 py-3 border-b ${d ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-violet-500" />
            <span className={`font-dm-serif text-[15px] ${d ? 'text-gray-200' : 'text-gray-700'}`}>
              AI draft — {row.contact_name || row.contact_email}
            </span>
          </div>
          <button onClick={onClose} aria-label="Close" className={`p-1 rounded ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}><X size={15} /></button>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          <p className={`text-[12px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>
            Personalized opening for the <strong>{row.state}</strong> digest (step {row.reminder_step}) —
            {' '}{row.property_count} propert{row.property_count === 1 ? 'y' : 'ies'}, {row.units_outstanding} units outstanding.
            The property table, CSV checklists, and portal instructions are always appended automatically. Review before saving.
          </p>

          {generating ? (
            <div className="flex items-center justify-center gap-2 py-12">
              <Loader2 size={16} className="animate-spin text-violet-500" />
              <span className={`text-[13px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Drafting…</span>
            </div>
          ) : (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={10}
              className={`w-full px-3 py-2 rounded-lg border text-[13px] font-rasa leading-relaxed ${
                d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-300 text-gray-700'
              } focus:outline-none focus:ring-1 focus:ring-violet-500/50`}
            />
          )}

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !generating && generate(instructions)}
              placeholder='Steer the rewrite: "mention the June deadline", "warmer tone"…'
              className={`flex-1 px-3 py-2 rounded-lg border text-[12px] font-rasa ${
                d ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
              } focus:outline-none focus:ring-1 focus:ring-violet-500/50`}
            />
            <button
              onClick={() => generate(instructions)}
              disabled={generating}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-rasa font-medium ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} disabled:opacity-50`}
            >
              <RefreshCw size={13} /> Regenerate
            </button>
          </div>
        </div>

        <div className={`flex items-center justify-between px-5 py-3 border-t ${d ? 'border-gray-700' : 'border-gray-200'}`}>
          {row.custom_message ? (
            <button onClick={() => save(null)} disabled={saving}
              className={`text-[12px] font-rasa ${d ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
              Clear saved draft (use stock wording)
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className={`px-3 py-2 rounded-lg text-[13px] font-rasa ${d ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}>
              Cancel
            </button>
            <button
              onClick={() => save(draft)}
              disabled={saving || generating || !draft.trim()}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Save size={13} /> {saving ? 'Saving…' : 'Save draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutreachDraftModal;
